/**
 * Comments and suggestions on a shared trip.
 *
 * The same component serves both sides: the owner sees it under their
 * itinerary, and an invitee sees it under the read-only shared view. The only
 * differences are who is named as the author and whether the delete control
 * appears — so there is one thread, not two views of two threads.
 *
 * Mounted only when a trip has actually been shared. A comment box on a trip
 * nobody can see is furniture.
 */

import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';

import { MAX_COMMENT_LENGTH } from '../../../utils/tripSharing';
import { deleteComment, listenComments, postComment } from '../../../utils/tripInvites';

function initialsOf(name) {
  return String(name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

function formatWhen(createdAt) {
  // serverTimestamp() resolves to null on the local echo of a write before the
  // server acknowledges it. Showing "Just now" is truer than an empty cell and
  // truer than a date of 1970.
  if (!createdAt) return 'Just now';
  const millis =
    typeof createdAt.toMillis === 'function'
      ? createdAt.toMillis()
      : typeof createdAt.seconds === 'number'
        ? createdAt.seconds * 1000
        : Date.parse(createdAt);
  if (!Number.isFinite(millis)) return '';
  return new Date(millis).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * @param {string}  props.threadId
 * @param {string?} props.inviteId    null when the owner is posting. The rules
 *                                    check this: a guest must name a live
 *                                    invite, and only the thread's owner may
 *                                    post without one — so the "Organiser"
 *                                    badge is a claim the database verifies
 *                                    rather than a field any link holder can set.
 * @param {string}  props.authorName  how this poster is named on the thread
 * @param {boolean} props.canModerate the owner may remove a comment
 * @param {boolean} props.readOnly    true once the trip is finalised
 */
export default function TripComments({
  threadId,
  inviteId = null,
  authorName,
  canModerate = false,
  readOnly = false,
}) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [kind, setKind] = useState('COMMENT');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!threadId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    // The unsubscribe is returned as cleanup rather than discarded — a
    // listener left running on unmount keeps writing into a dead component.
    const unsubscribe = listenComments(threadId, (next) => {
      setComments(next);
      setLoading(false);
    });
    return unsubscribe;
  }, [threadId]);

  const handlePost = async () => {
    setError('');
    setBusy(true);
    try {
      await postComment({ threadId, inviteId, authorName, body, kind });
      setBody('');
      setKind('COMMENT');
    } catch (e) {
      setError(e.message || 'We could not post that just now. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!threadId) return null;

  return (
    <Paper elevation={1} sx={{ p: 2, m: 2 }}>
      <Typography
        sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
      >
        <ForumRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.75, color: 'text.secondary' }} />
        Comments and suggestions
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {readOnly
          ? 'This trip has been finalised, so the thread is closed.'
          : 'Everyone invited to this trip can read this.'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rounded" height={54} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={54} />
        </Box>
      ) : comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Nothing yet. {readOnly ? '' : 'Be the first to say something.'}
        </Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}>
          {comments.map((comment) => (
            <Box
              component="li"
              key={comment.commentId}
              sx={{ display: 'flex', gap: 1.5, py: 1.25, alignItems: 'flex-start' }}
            >
              <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                {initialsOf(comment.authorName)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 600 }}>{comment.authorName}</Typography>
                  {comment.kind === 'SUGGESTION' && (
                    <Chip size="small" color="primary" variant="outlined" label="Suggestion" />
                  )}
                  {!comment.inviteId && <Chip size="small" label="Organiser" />}
                  <Typography variant="caption" color="text.secondary">
                    {formatWhen(comment.createdAt)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.25 }}>
                  {comment.body}
                </Typography>
              </Box>
              {canModerate && (
                <IconButton
                  size="small"
                  aria-label={`Remove comment from ${comment.authorName}`}
                  onClick={() => deleteComment(threadId, comment.commentId)}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!readOnly && (
        <Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_e, next) => next && setKind(next)}
            aria-label="Kind of message"
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="COMMENT">Comment</ToggleButton>
            <ToggleButton value="SUGGESTION">Suggestion</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            id="trip-comment"
            label={kind === 'SUGGESTION' ? 'What would you change?' : 'Add a comment'}
            multiline
            rows={3}
            fullWidth
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
            helperText={`${body.length}/${MAX_COMMENT_LENGTH}`}
            disabled={busy}
          />

          <Button
            variant="contained"
            sx={{ mt: 1.5 }}
            onClick={handlePost}
            disabled={busy || !body.trim()}
          >
            {busy ? 'Posting…' : 'Post'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
